/**
 * localStorage abstraction layer with versioning, safe parsing, and migration support.
 * 
 * All data access should go through dataService.ts (which already uses getCollection/setCollection).
 * This module provides additional utilities for schema versioning and migration.
 */

const STORAGE_VERSION_KEY = 'wf_schema_version';
const CURRENT_VERSION = 1;

/** All known localStorage keys used by the app */
export const STORAGE_KEYS = {
  CONTACTS: 'wf_contacts',
  CONVERSATIONS: 'wf_conversations',
  AUTOMATIONS: 'wf_automations',
  AUTOMATION_RUNS: 'wf_automation_runs',
  CAMPAIGNS: 'wf_campaigns',
  TEMPLATES: 'wf_templates',
  LOGS: 'wf_logs',
  NOTIFICATIONS: 'wf_notifications',
  TEAM: 'wf_team',
  SAVED_REPLIES: 'wf_saved_replies',
  BLACKLIST: 'wf_blacklist',
  AI_SETTINGS: 'wf_ai_settings',
  AI_HISTORY: 'wf_ai_history',
  AI_MEMORY: 'wf_ai_memory',
  INTEGRATIONS: 'wf_integrations',
  SETTINGS_WORKSPACE: 'wf_settings_workspace',
  // Dynamic keys:
  // wf_messages_{conversationId}
  // wf_notes_{contactId}
  // wf_campaign_recipients_{campaignId}
} as const;

/** Safe JSON parse with fallback */
export function safeParse<T>(json: string | null, fallback: T): T {
  if (!json) return fallback;
  try {
    return JSON.parse(json);
  } catch {
    console.warn('[Storage] Failed to parse JSON, using fallback');
    return fallback;
  }
}

/** Get the current schema version */
export function getSchemaVersion(): number {
  return safeParse(localStorage.getItem(STORAGE_VERSION_KEY), 0);
}

/** Run migrations if schema version is outdated */
export function runMigrations(): void {
  const version = getSchemaVersion();
  
  if (version < CURRENT_VERSION) {
    // Version 0 → 1: Initial schema, no migration needed (first version)
    // Future migrations would go here:
    // if (version < 2) { migrateV1ToV2(); }
    
    localStorage.setItem(STORAGE_VERSION_KEY, String(CURRENT_VERSION));
  }
}

/** Get storage usage stats */
export function getStorageStats(): { totalSize: number; keys: number; breakdown: Record<string, number> } {
  const breakdown: Record<string, number> = {};
  let totalSize = 0;
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('wf_')) {
      const value = localStorage.getItem(key) || '';
      const size = new Blob([value]).size;
      breakdown[key] = size;
      totalSize += size;
    }
  }
  
  return { totalSize, keys: Object.keys(breakdown).length, breakdown };
}
